import React from 'react';
import cx from 'classnames';

import { ActiveBar, ErrorBar } from './components';

import './gradient-progress-bar.css';

interface Props {
    canceled?: boolean;
    color: GradientProgressBarColor;
    error?: Maybe<string>;
    errorStyle?: 'loud' | 'quiet';
    handleCancel?(): void;
    handleExpandError?(): void;
    height?: number | string;
    Icon?: IconComponent;
    inProgressLabel: string;
    label: string;
    labelLines?: 1 | 2;
    loadingSpinner?: boolean;
    progress: number;
    width?: number;
}

/**
 * A progress bar that fills in using a gradient, from left to right.
 * For the 'aqua' color, the progress meter is solid and the background is a gradient; for the
 * 'purple-to-aqua' color, the progress meter is a gradient and the background is solid.
 *
 * An explicit width is always used; if not provided, it has a default value. If no height is
 * provided, padding is used to determine the height.
 */
export const GradientProgressBar = ({
    canceled = false,
    color,
    error,
    errorStyle = 'loud',
    handleCancel,
    handleExpandError,
    height,
    Icon,
    inProgressLabel,
    label,
    labelLines = 1,
    loadingSpinner = false,
    progress,
    width = 500,
}: Props): JSX.Element => {
    const classNames = cx('gradient-progress-bar', errorStyle, {
        [color]: !error,
        error,
    });
    const meterWidth = (progress / 100) * width;
    const gap = width - meterWidth;

    return (
        <div className={classNames}>
            <div
                className="progress-container relative rounded flex items-center"
                style={{ width, height }}
            >
                <div
                    className={cx('text-sm', {
                        'py-4': height === undefined,
                    })}
                    style={{ width }}
                >
                    {!error && (
                        <ActiveBar
                            canceled={canceled}
                            color={color}
                            handleCancel={handleCancel}
                            Icon={Icon}
                            inProgressLabel={inProgressLabel}
                            label={label}
                            labelLines={labelLines}
                            loadingSpinner={loadingSpinner}
                        />
                    )}
                    {error && (
                        <ErrorBar
                            canceled={canceled}
                            errorStyle={errorStyle}
                            handleCancel={handleCancel}
                            handleExpandError={handleExpandError}
                            Icon={Icon}
                            label={label}
                            labelLines={labelLines}
                            width={width}
                        />
                    )}
                </div>
                <div
                    className={cx(
                        'absolute top-0 progress-meter h-full rounded-l',
                        {
                            'rounded-r': gap < 5,
                        },
                    )}
                    style={{
                        width: meterWidth,
                    }}
                />
            </div>
        </div>
    );
};
